import { PlatformRole, isPilotExpired } from '@repo/shared'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SidebarProvider,
  cn,
} from '@repo/ui'
import {
  Outlet,
  createFileRoute,
  useRouter,
  useRouterState,
} from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import useUser from '../lib/api/auth/me'

import type React from 'react'

import { Header } from '@/components/layout/header/header'
import { PilotExpiredGate } from '@/components/layout/pilot-expired-gate'
import { Sidebar } from '@/components/layout/sidebar'
import { FleetMapView } from '@/features/fleet-map'
import { isMapRoute } from '@/lib/map-routes'
import { useDropdownStore } from '@/store/dropdown-store'
import { useUIStore } from '@/store/ui-store'

export const Route = createFileRoute('/dashboard')({
  component: DashboardLayout,
})

function DashboardLayout() {
  const { user, isPending } = useUser()
  const router = useRouter()
  const isCollapsed = useUIStore((s) => s.sidebarCollapsed)
  const setIsCollapsed = useUIStore((s) => s.setSidebarCollapsed)
  const mobileNavOpen = useUIStore((s) => s.mobileNavOpen)
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen)
  const { t } = useTranslation('common')
  const isDropdownOpen = useDropdownStore((s) => s.isDropdownOpen)
  const isFleetMap = useRouterState({
    select: (s) => isMapRoute(s.location.pathname),
  })
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  useEffect(() => {
    if (isPending) return
    if (!user) {
      router.navigate({ to: '/login' })
      return
    }
    if (user.platformRole === PlatformRole.SuperAdmin) {
      router.navigate({ to: '/admin' })
    }
  }, [user, isPending, router])

  useEffect(() => {
    setMobileNavOpen(false)
  }, [pathname, setMobileNavOpen])

  if (isPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          <p className="text-muted-foreground mt-4">{t('loading')}</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  // Computed once from user — guaranteed available, no secondary hook needed.
  // Avoids a render where isPending is true inside usePilotCountdown causing
  // FleetMapView to mount briefly before the expired gate takes over.
  const pilotExpired = isPilotExpired(
    user.organizationPlan,
    user.pilotExpiresAt,
  )

  return (
    <div className="flex h-dvh w-full overflow-x-hidden">
      <div className="hidden md:block">
        <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
      </div>
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="right"
          showCloseButton={false}
          className="w-full max-w-none p-0 sm:max-w-none md:hidden"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{t('layout.openNav')}</SheetTitle>
            <SheetDescription>{t('layout.openNav')}</SheetDescription>
          </SheetHeader>
          <Sidebar
            isCollapsed={false}
            setIsCollapsed={() => {}}
            onMobileClose={() => setMobileNavOpen(false)}
          />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <SidebarProvider
          style={
            {
              '--sidebar-width': '26rem',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            } as React.CSSProperties
          }
          defaultOpen={false}
        >
          <Header />

          <main
            className={cn('relative flex-1', isFleetMap && 'overflow-hidden')}
          >
            {pilotExpired ? (
              <PilotExpiredGate />
            ) : (
              <>
                <div
                  className={cn(
                    'bg-background absolute inset-0 z-40 overflow-y-auto p-6 px-3 py-4 sm:px-4',
                    isFleetMap && 'hidden',
                  )}
                >
                  <Outlet />
                </div>

                <FleetMapView />

                {isDropdownOpen && (
                  <div className="absolute inset-0 z-50 backdrop-blur-md" />
                )}
              </>
            )}
          </main>
        </SidebarProvider>
      </div>
    </div>
  )
}
